'use client';

import axios, { endpoints } from 'src/utils/axios';

import { REFRESH_TOKEN, STORAGE_KEY, USER } from './constant';
import { setSession } from './utils';

// ----------------------------------------------------------------------

export type SignInParams = {
  email: string;
  password: string;
};

export type SignInPendingTwoFactor = {
  requiresTwoFactor: 'EMAIL_OTP' | 'TOTP_APP' | 'SELECT_FACTOR';
  challengeToken: string;
  email: string;
  availableFactors?: { type: 'EMAIL_OTP' | 'TOTP_APP' }[];
  selectedFactor?: 'EMAIL_OTP' | 'TOTP_APP';
};

export const LOGIN_MFA_ENROLLMENT_TOKEN_KEY = 'loginMfaEnrollmentToken';
export const LOGIN_MFA_ENROLLMENT_SKIP_ALLOWED_KEY = 'loginMfaEnrollmentSkipAllowed';

export type SignInPendingMfaEnrollment = {
  requiresMfaEnrollment: true;
  enrollmentToken: string;
  email: string;
  availableFactors?: { type: string }[];
  mfaEnrollmentSkipAllowed?: boolean;
};

export type SignedInUser = {
  id: number;
  client?: Record<string, unknown>;
  isEmailVerified?: boolean;
  [key: string]: unknown;
};

export type VerifyLoginTwoFactorParams = {
  challengeToken: string;
  token?: string;
  factorType?: 'EMAIL_OTP' | 'TOTP_APP';
  recoveryCode?: string;
};

export type SignUpParams = {
  email: string;
  password: string;
  name: string;
  language: string;
  // lastName: string;
};

export type VerifyParams = {
  email: string;
  token: string;
};

export type ResetPasswordParams = {
  password: string;
  confirmPassword: string;
  token: string;
};

export type AcceptInvitationParams = {
  token: string;
  password: string;
  confirmPassword: string;
};

export type ValidateInvitationTokenResult = {
  active: boolean;
  valid: boolean;
};

export type CompleteEntraSsoLoginParams = {
  completionToken: string;
};

export const completeEntraSsoLogin = async ({
  completionToken,
}: CompleteEntraSsoLoginParams): Promise<
  SignedInUser | SignInPendingTwoFactor | SignInPendingMfaEnrollment
> => {
  try {
    const res = await axios.post(endpoints.auth.ssoComplete('microsoft'), {
      completionToken,
    });

    const {
      accessToken,
      refreshToken,
      user,
      requiresTwoFactor,
      challengeToken,
      selectedFactor,
      requiresMfaEnrollment,
      enrollmentToken,
      availableFactors,
      mfaEnrollmentSkipAllowed,
    } = res.data;

    if (requiresTwoFactor && challengeToken) {
      return {
        requiresTwoFactor,
        challengeToken,
        email: res.data.email || '',
        availableFactors,
        selectedFactor,
      } as SignInPendingTwoFactor;
    }

    if (requiresMfaEnrollment && enrollmentToken) {
      return {
        requiresMfaEnrollment: true,
        enrollmentToken,
        email: res.data.email || '',
        availableFactors,
        mfaEnrollmentSkipAllowed,
      } as SignInPendingMfaEnrollment;
    }

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    await setSession(accessToken, refreshToken, user);
    return user;
  } catch (error) {
    console.error('Error completing Entra SSO login:', error);
    throw error;
  }
};

/** **************************************
 * Sign in
 *************************************** */
export const signInWithPassword = async ({
  email,
  password,
}: SignInParams): Promise<
  SignedInUser | SignInPendingTwoFactor | SignInPendingMfaEnrollment
> => {
  try {
    const params = { email, password };

    const res = await axios.post(endpoints.auth.signIn, params);

    const {
      accessToken,
      refreshToken,
      user,
      requiresTwoFactor,
      challengeToken,
      selectedFactor,
      requiresMfaEnrollment,
      enrollmentToken,
      availableFactors,
      mfaEnrollmentSkipAllowed,
    } = res.data;

    if (requiresTwoFactor && challengeToken) {
      return {
        requiresTwoFactor,
        challengeToken,
        email: res.data.email || email,
        availableFactors,
        selectedFactor,
      } as SignInPendingTwoFactor;
    }

    if (requiresMfaEnrollment && enrollmentToken) {
      return {
        requiresMfaEnrollment: true,
        enrollmentToken,
        email: res.data.email || email,
        availableFactors,
        mfaEnrollmentSkipAllowed,
      } as SignInPendingMfaEnrollment;
    }

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    await setSession(accessToken, refreshToken, user);
    return user;
  } catch (error) {
    console.error('Error during sign in:', error);
    throw error;
  }
};

export type CompleteLoginMfaEnrollmentParams = {
  enrollmentToken: string;
  decision: 'SKIP' | 'ENABLE_EMAIL_OTP';
};

export const completeLoginMfaEnrollment = async ({
  enrollmentToken,
  decision,
}: CompleteLoginMfaEnrollmentParams): Promise<SignedInUser> => {
  try {
    const res = await axios.post(endpoints.auth.completeLoginMfaEnrollment, {
      enrollmentToken,
      decision,
    });

    const { accessToken, refreshToken, user } = res.data;
    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    await setSession(accessToken, refreshToken, user);
    return user;
  } catch (error) {
    console.error('Error during MFA enrollment completion:', error);
    throw error;
  }
};

export const verifyLoginTwoFactor = async ({
  challengeToken,
  token,
  factorType,
  recoveryCode,
}: VerifyLoginTwoFactorParams): Promise<SignedInUser> => {
  try {
    const res = await axios.post(
      endpoints.auth.verifyLoginTwoFactor,
      {
        challengeToken,
        token,
        factorType,
        recoveryCode,
      },
      { reauthenticate: false },
    );

    const { accessToken, refreshToken, user } = res.data;
    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    await setSession(accessToken, refreshToken, user);
    return user;
  } catch (error) {
    console.error('Error during two-factor verification:', error);
    throw error;
  }
};

export const startLoginTotpEnrollment = async (enrollmentToken: string): Promise<{
  qrCodeDataUrl: string;
  manualEntryKey: string;
}> => {
  const res = await axios.post(endpoints.auth.startLoginTotpEnrollment, {
    enrollmentToken,
  });
  return res.data;
};

export const confirmLoginTotpEnrollment = async (
  enrollmentToken: string,
  code: string,
): Promise<{ user: SignedInUser; recoveryCodes: string[] }> => {
  const res = await axios.post(endpoints.auth.confirmLoginTotpEnrollment, {
    enrollmentToken,
    code,
  });
  const { accessToken, refreshToken, user, recoveryCodes } = res.data;
  if (!accessToken) {
    throw new Error('Access token not found in response');
  }
  await setSession(accessToken, refreshToken, user);
  return { user, recoveryCodes: recoveryCodes || [] };
};

export const startUserTotpEnrollment = async (): Promise<{
  qrCodeDataUrl: string;
  manualEntryKey: string;
}> => {
  const res = await axios.post(endpoints.auth.startUserTotpEnrollment);
  return res.data;
};

export const confirmUserTotpEnrollment = async (
  code: string,
): Promise<{ recoveryCodes: string[] }> => {
  const res = await axios.post(endpoints.auth.confirmUserTotpEnrollment, { code }, {reauthenticate: false});
  return res.data;
};

/** **************************************
 * Sign up
 *************************************** */
export const signUp = async ({
  email,
  password,
  name,
  language,
  // lastName,
}: SignUpParams): Promise<void> => {
  const params = {
    email,
    password,
    name,
    language,
    // lastName,
  };

  try {
    const res = await axios.post(endpoints.auth.signUp, params);

    const { accessToken } = res.data;
    console.log('bkdjfnv mnd', res.data);

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

  } catch (error) {
    console.error('Error during sign up:', error);
    throw error;
  }
};


/** **************************************
 * Force refresh token
 *************************************** */
export const forceRefreshToken = async (): Promise<any> => {
  try {
    // Get refresh token and user from localStorage
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    const user = localStorage.getItem(USER);
    const parsedUser = user ? JSON.parse(user) : null;

    if (!refreshToken || !parsedUser) {
      throw new Error('No refresh token or user data found');
    }

    const res = await axios.put(
      endpoints.auth.refreshToken,
      {
        id: parsedUser.id,
        refreshToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );

    const { accessToken, refreshToken: newRefreshToken, user: updatedUser } = res.data;

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    setSession(accessToken, newRefreshToken, updatedUser);
    return updatedUser;
  } catch (error) {
    console.error('Error during force refresh token:', error);
    throw error;
  }
};

/** **************************************
 * Resend verification email
 *************************************** */
export const resendVerificationEmail = async ({ email }: { email: string }): Promise<void> => {
  try {
    await axios.post(endpoints.auth.resendVerification, { email }, { reauthenticate: false });
  } catch (error) {
    console.error('Error resending verification email:', error);
    throw error;
  }
};

/** **************************************
 * Verify
 *************************************** */
export const verify = async ({
  token,
  // lastName,
}: VerifyParams): Promise<void> => {
  // const params = {
  //   email,
  //   token,
  //   // lastName,
  // };

  try {
    const res = await axios.post(`${endpoints.auth.verify}?token=${token}`);

    const { accessToken } = res.data;
    console.log('bkdjfnv mnd', res.data);

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    localStorage.setItem(STORAGE_KEY, accessToken);
    localStorage.setItem(USER, JSON.stringify(res.data.user));
  } catch (error) {
    console.error('Error during sign up:', error);
    throw error;
  }
};

/** **************************************
 * Request Password Reset
 *************************************** */
export const requestPasswordReset = async ({ email }: { email: string }): Promise<void> => {
  try {
    await axios.post(endpoints.auth.passwordResetEmail, { email });
  } catch (error) {
    console.error('Error during password reset request:', error);
    throw error;
  }
};

/** **************************************
 * Change Password
 *************************************** */
export const changePassword = async ({
  password,
  confirmPassword,
  token,
}: ResetPasswordParams): Promise<void> => {
  const params = {
    password,
    confirmPassword,
  };

  try {
    await axios.put(`${endpoints.auth.passwordReset(token)}`, params);
  } catch (error) {
    console.error('Error during password change:', error);
    throw error;
  }
};

export const createCompanyName = async ({
  createCompanyNameV,
  type,
  userId,
}: any): Promise<void> => {
  const params = {
    userId,
    createCompanyName: createCompanyNameV,
    type,
  };

  try {
    const res: any = await axios.post(endpoints.auth.createCompanyName, params);

    const { client } = res.data.user;
    const user = JSON.parse(localStorage.getItem(USER) || '{}');
    user.client = client;
    localStorage.setItem(USER, JSON.stringify(user));

    const { accessToken } = res.data;

    if (!accessToken) {
      throw new Error('Access token not found in response');
    }

    localStorage.setItem(STORAGE_KEY, accessToken);
  } catch (error) {
    console.error('Error during sign up:', error);
    throw error;
  }
};

/** **************************************
 * Accept Invitation
 *************************************** */
export const acceptInvitation = async ({
  password,
  confirmPassword,
  token,
}: AcceptInvitationParams): Promise<void> => {
  const params = {
    password,
    confirmPassword,
  };

  try {
    await axios.post(`${endpoints.auth.acceptInvitation(token)}`, params);
  } catch (error) {
    console.error('Error during invitation acceptance:', error);
    throw error;
  }
};

/** **************************************
 * Validate invitation token (preflight)
 *************************************** */
export const validateInvitationToken = async (
  token: string
): Promise<ValidateInvitationTokenResult> => {
  const res = await axios.post<ValidateInvitationTokenResult>(
    endpoints.auth.validateInvitation(token)
  );
  return res.data;
};
